import { ReturnAnalyticsRecord, ReturnAnalyticsReturnItem } from 'types/return';

export interface ReturnAnalyticsMetric {
  reason: string;
  returns: number;
  quantity: number;
  value: number;
}

export interface ReturnAnalyticsItemMetric {
  itemKey: string;
  itemTitle: string;
  sku: string;
  returns: number;
  quantity: number;
  value: number;
  topReason: string;
}

export interface ReturnAnalyticsReasonByItem {
  itemKey: string;
  itemTitle: string;
  sku: string;
  returns: number;
  quantity: number;
  value: number;
  reasons: ReturnAnalyticsMetric[];
}

export interface ReturnAnalyticsItemByReason extends ReturnAnalyticsMetric {
  items: ReturnAnalyticsItemMetric[];
}

export interface ReturnAnalyticsSummary {
  totalReturns: number;
  totalReturnItems: number;
  totalReturnedValue: number;
  totalLabelCost: number;
  totalEstimatedCost: number;
  averageItemsPerReturn: number;
  averageReturnedValue: number;
  averageLabelCost: number;
}

export interface ReturnAnalyticsCosts {
  totalReturnedValue: number;
  totalLabelCost: number;
  totalEstimatedCost: number;
  averageReturnedValue: number;
  averageLabelCost: number;
  highestCostItem?: ReturnAnalyticsItemMetric;
  highestCostReason?: ReturnAnalyticsMetric;
}

export interface ReturnAnalyticsResult {
  summary: ReturnAnalyticsSummary;
  returnsByItem: ReturnAnalyticsItemMetric[];
  reasonsByItem: ReturnAnalyticsReasonByItem[];
  itemsByReason: ReturnAnalyticsItemByReason[];
  returnReasons: ReturnAnalyticsMetric[];
  costs: ReturnAnalyticsCosts;
}

export interface ReturnAnalyticsCsvOptions {
  startDate: string;
  endDate: string;
  currency: string;
  includeCarrierCosts?: boolean;
}

type MutableMetricGroup = {
  returnIds: Set<string>;
  quantity: number;
  value: number;
};

type MutableReasonGroup = MutableMetricGroup & {
  reason: string;
  items: Map<string, MutableItemGroup>;
};

type MutableItemGroup = MutableMetricGroup & {
  itemKey: string;
  itemTitle: string;
  sku: string;
  reasons: Map<string, MutableReasonGroup>;
};

const UNSPECIFIED_REASON = 'Unspecified reason';
const UNKNOWN_ITEM = 'Unknown item';

const roundNumber = (value: number) => Number(value.toFixed(2));

const numericValue = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const quantityValue = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 1;

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;

  return Math.max(parsed, 0);
};

const formatAnalyticsLabel = (value?: string | null): string => {
  const normalized = String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase();

  if (!normalized) return UNSPECIFIED_REASON;

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const buildItemKey = (
  returnId: string,
  itemIndex: number,
  item: ReturnAnalyticsReturnItem,
) => {
  const orderItem = item?.orderItem;
  return (
    orderItem?.id ||
    item?.orderItemId ||
    orderItem?.sku ||
    orderItem?.title ||
    `${returnId}-item-${itemIndex}`
  );
};

const sortMetrics = <
  T extends { returns: number; quantity: number; value: number },
>(
  metrics: T[],
) =>
  metrics.sort(
    (a, b) =>
      b.quantity - a.quantity || b.returns - a.returns || b.value - a.value,
  );

const toReasonMetric = (
  reason: string,
  group: MutableReasonGroup,
): ReturnAnalyticsMetric => ({
  reason,
  returns: group.returnIds.size,
  quantity: roundNumber(group.quantity),
  value: roundNumber(group.value),
});

const toItemMetric = (group: MutableItemGroup): ReturnAnalyticsItemMetric => {
  const reasons = sortMetrics(
    Array.from(group.reasons.entries()).map(([reason, reasonGroup]) =>
      toReasonMetric(reason, reasonGroup),
    ),
  );

  return {
    itemKey: group.itemKey,
    itemTitle: group.itemTitle,
    sku: group.sku,
    returns: group.returnIds.size,
    quantity: roundNumber(group.quantity),
    value: roundNumber(group.value),
    topReason: reasons[0]?.reason || UNSPECIFIED_REASON,
  };
};

export const buildReturnAnalytics = (
  returns: ReturnAnalyticsRecord[] = [],
): ReturnAnalyticsResult => {
  const itemGroups = new Map<string, MutableItemGroup>();
  const reasonGroups = new Map<string, MutableReasonGroup>();
  let totalReturnItems = 0;
  let totalReturnedValue = 0;
  let totalLabelCost = 0;
  let totalReturns = 0;

  returns.forEach((returnRecord) => {
    if (!returnRecord) return;

    totalReturns += 1;
    const returnId = returnRecord.id;
    totalLabelCost += numericValue(returnRecord.labelCost);

    (returnRecord.returnItems || []).forEach((item, itemIndex) => {
      if (!item) return;
      const quantity = quantityValue(item.quantity);
      const itemPrice = numericValue(item.orderItem?.price);
      const itemValue = itemPrice * quantity;
      const reason = formatAnalyticsLabel(item.reason);
      const itemKey = buildItemKey(returnId, itemIndex, item);
      const itemTitle = item.orderItem?.title?.trim() || UNKNOWN_ITEM;
      const sku = item.orderItem?.sku?.trim() || 'No SKU';

      totalReturnItems += quantity;
      totalReturnedValue += itemValue;

      const itemGroup =
        itemGroups.get(itemKey) ||
        ({
          itemKey,
          itemTitle,
          sku,
          returnIds: new Set<string>(),
          quantity: 0,
          value: 0,
          reasons: new Map<string, MutableReasonGroup>(),
        } as MutableItemGroup);

      itemGroup.returnIds.add(returnId);
      itemGroup.quantity += quantity;
      itemGroup.value += itemValue;
      itemGroups.set(itemKey, itemGroup);

      const reasonGroup =
        reasonGroups.get(reason) ||
        ({
          reason,
          returnIds: new Set<string>(),
          quantity: 0,
          value: 0,
          items: new Map<string, MutableItemGroup>(),
        } as MutableReasonGroup);

      reasonGroup.returnIds.add(returnId);
      reasonGroup.quantity += quantity;
      reasonGroup.value += itemValue;
      reasonGroups.set(reason, reasonGroup);

      const itemReasonGroup =
        itemGroup.reasons.get(reason) ||
        ({
          reason,
          returnIds: new Set<string>(),
          quantity: 0,
          value: 0,
          items: new Map<string, MutableItemGroup>(),
        } as MutableReasonGroup);

      itemReasonGroup.returnIds.add(returnId);
      itemReasonGroup.quantity += quantity;
      itemReasonGroup.value += itemValue;
      itemGroup.reasons.set(reason, itemReasonGroup);

      const reasonItemGroup =
        reasonGroup.items.get(itemKey) ||
        ({
          itemKey,
          itemTitle,
          sku,
          returnIds: new Set<string>(),
          quantity: 0,
          value: 0,
          reasons: new Map<string, MutableReasonGroup>(),
        } as MutableItemGroup);

      reasonItemGroup.returnIds.add(returnId);
      reasonItemGroup.quantity += quantity;
      reasonItemGroup.value += itemValue;
      reasonGroup.items.set(itemKey, reasonItemGroup);
    });
  });

  const returnsByItem = sortMetrics(
    Array.from(itemGroups.values()).map(toItemMetric),
  );

  const returnReasons = sortMetrics(
    Array.from(reasonGroups.entries()).map(([reason, reasonGroup]) =>
      toReasonMetric(reason, reasonGroup),
    ),
  );

  const reasonsByItem = returnsByItem.map((itemMetric) => {
    const group = itemGroups.get(itemMetric.itemKey);

    return {
      ...itemMetric,
      reasons: group
        ? sortMetrics(
            Array.from(group.reasons.entries()).map(([reason, reasonGroup]) =>
              toReasonMetric(reason, reasonGroup),
            ),
          )
        : [],
    };
  });

  const itemsByReason = returnReasons.map((reasonMetric) => {
    const group = reasonGroups.get(reasonMetric.reason);

    return {
      ...reasonMetric,
      items: group
        ? sortMetrics(Array.from(group.items.values()).map(toItemMetric))
        : [],
    };
  });

  const totalEstimatedCost = totalReturnedValue + totalLabelCost;

  return {
    summary: {
      totalReturns,
      totalReturnItems: roundNumber(totalReturnItems),
      totalReturnedValue: roundNumber(totalReturnedValue),
      totalLabelCost: roundNumber(totalLabelCost),
      totalEstimatedCost: roundNumber(totalEstimatedCost),
      averageItemsPerReturn: roundNumber(
        totalReturns ? totalReturnItems / totalReturns : 0,
      ),
      averageReturnedValue: roundNumber(
        totalReturns ? totalReturnedValue / totalReturns : 0,
      ),
      averageLabelCost: roundNumber(
        totalReturns ? totalLabelCost / totalReturns : 0,
      ),
    },
    returnsByItem,
    reasonsByItem,
    itemsByReason,
    returnReasons,
    costs: {
      totalReturnedValue: roundNumber(totalReturnedValue),
      totalLabelCost: roundNumber(totalLabelCost),
      totalEstimatedCost: roundNumber(totalEstimatedCost),
      averageReturnedValue: roundNumber(
        totalReturns ? totalReturnedValue / totalReturns : 0,
      ),
      averageLabelCost: roundNumber(
        totalReturns ? totalLabelCost / totalReturns : 0,
      ),
      highestCostItem: returnsByItem[0],
      highestCostReason: returnReasons[0],
    },
  };
};

const csvCell = (value: string | number | null | undefined) => {
  const text = value === null || value === undefined ? '' : String(value);
  const escaped = text.replace(/"/g, '""');

  return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
};

const csvRow = (values: Array<string | number | null | undefined>) =>
  values.map(csvCell).join(',');

export const buildReturnAnalyticsCsv = (
  analytics: ReturnAnalyticsResult,
  options: ReturnAnalyticsCsvOptions,
) => {
  const includeCarrierCosts = options.includeCarrierCosts !== false;
  const rows: string[][] = [
    ['Return and exchange analytics export'],
    ['Scope', 'Return and exchange requests; original item values are not refunds or replacement values.'],
    ['Date range', `${options.startDate} to ${options.endDate}`],
    ['Currency', options.currency],
    [],
    ['Summary'],
    ['Metric', 'Value'],
    ['Total requests', String(analytics.summary.totalReturns)],
    ['Requested units', String(analytics.summary.totalReturnItems)],
    ['Original item value', String(analytics.summary.totalReturnedValue)],
    ...(includeCarrierCosts
      ? [
          ['Carrier label costs', String(analytics.summary.totalLabelCost)],
          ['Estimated cost', String(analytics.summary.totalEstimatedCost)],
        ]
      : []),
    [
      'Average items per request',
      String(analytics.summary.averageItemsPerReturn),
    ],
    ['Average original item value', String(analytics.summary.averageReturnedValue)],
    ...(includeCarrierCosts
      ? [
          [
            'Average carrier label cost',
            String(analytics.summary.averageLabelCost),
          ],
        ]
      : []),
    [],
    ['Requests by item'],
    ['Item', 'SKU', 'Requests', 'Units', 'Original item value', 'Top reason'],
    ...analytics.returnsByItem.map((item) => [
      item.itemTitle,
      item.sku,
      String(item.returns),
      String(item.quantity),
      String(item.value),
      item.topReason,
    ]),
    [],
    ['Request reasons by item'],
    ['Item', 'SKU', 'Reason', 'Requests', 'Units', 'Original item value'],
    ...analytics.reasonsByItem.flatMap((item) =>
      item.reasons.map((reason) => [
        item.itemTitle,
        item.sku,
        reason.reason,
        String(reason.returns),
        String(reason.quantity),
        String(reason.value),
      ]),
    ),
    [],
    ['Items by request reason'],
    ['Reason', 'Item', 'SKU', 'Requests', 'Units', 'Original item value'],
    ...analytics.itemsByReason.flatMap((reason) =>
      reason.items.map((item) => [
        reason.reason,
        item.itemTitle,
        item.sku,
        String(item.returns),
        String(item.quantity),
        String(item.value),
      ]),
    ),
    [],
    ['Request reasons'],
    ['Reason', 'Requests', 'Units', 'Original item value'],
    ...analytics.returnReasons.map((reason) => [
      reason.reason,
      String(reason.returns),
      String(reason.quantity),
      String(reason.value),
    ]),
    [],
    ['Costs'],
    ['Metric', 'Value'],
    ['Total original item value', String(analytics.costs.totalReturnedValue)],
    ...(includeCarrierCosts
      ? [
          ['Total carrier label cost', String(analytics.costs.totalLabelCost)],
          ['Total estimated cost', String(analytics.costs.totalEstimatedCost)],
        ]
      : []),
    ['Average original item value', String(analytics.costs.averageReturnedValue)],
    ...(includeCarrierCosts
      ? [
          [
            'Average carrier label cost',
            String(analytics.costs.averageLabelCost),
          ],
        ]
      : []),
    [
      'Highest cost item',
      analytics.costs.highestCostItem
        ? `${analytics.costs.highestCostItem.itemTitle} (${analytics.costs.highestCostItem.value})`
        : '',
    ],
    [
      'Highest cost reason',
      analytics.costs.highestCostReason
        ? `${analytics.costs.highestCostReason.reason} (${analytics.costs.highestCostReason.value})`
        : '',
    ],
  ];

  return rows.map(csvRow).join('\n');
};
